import Tooltip from "@material-ui/core/Tooltip";
import { CaretCircleDownIcon } from "@phosphor-icons/react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Accordion, Button } from "react-bootstrap";
import { FaPen } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { buildRequestFieldDefs } from "../../../redux/actions/lenderRequestActions";
import { isInternal, isLenderRole, isProjectReadOnlyRole, isReadonlyRole, isTPORole } from "../../../redux/actions/userAction";
import ContextAwareToggle from "../../Profile/ContextAwareToggle";
import { REQUEST_STATUSES, isClosedStatus } from "../../shared/constants/RequestStatuses";
import { fetchReviewers } from "../../shared/services/LenderRequestService";
import { AssignReviewerModal } from "../AssignReviewer/AssignReviewerModal";
import CommunicationFeed from "./CommunicationFeed/CommunicationFeed";
import { DocumentItem } from "./DocumentItem/DocumentItem";
import RequestDetailsModal from "./RequestDetailsModal/RequestDetailsModal";
import { RequestHistoryItem } from "./RequestHistoryItem/RequestHistoryItem";
import "./RequestFeed.css";

const RequestFeed = ({ requests, projectInfo, projectRestrictions, onRefresh, onExpand }) => {
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalInitialized, setModalInitialized] = useState(false);

  const dispatch = useDispatch();  

  const expandedRef = React.useRef(new Set());

  const handleExpand = (requestId) => {
    if (!expandedRef.current.has(requestId)) {
      expandedRef.current.add(requestId);
      onExpand?.(requestId);
    }
  };

  useEffect(() => {
    dispatch(buildRequestFieldDefs());
    
    if (isInternal()) {
      (async function () {
        try {
          setLoading(true); // Set loading to true before fetching data

          let reviewersList = await fetchReviewers();

          // Sort the requestList alphabetically by a property userName
          reviewersList = reviewersList.sort((a, b) => {
            const nameA = a.userName?.toLowerCase() || ""; 
            const nameB = b.userName?.toLowerCase() || "";
            return nameA.localeCompare(nameB);
          });
          
          setReviewers(reviewersList);
  
          setModalInitialized(true);
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false); // Set loading to false after data is fetched
        }
      })();
    }
  }, []); 
  
  // Stable component reference: prevents unmount/remount cycle on parent re-render.
  // Without useMemo, expanding an unread request causes onExpand → parent setState →
  // RequestFeed re-renders → this function is recreated → React sees a new component
  // type → unmounts old instance (losing accordion state) → requires a second click.
  const RequestHeaderInfo = React.useMemo(() => function RequestHeaderInfo({modalInitialized, reviewers, item, onRefresh, onExpand, projectInfo }) {
    const [showDetailsModal, setShowDetailsModal] = useState(false);    
    const [showAssignModal, setShowAssignModal] = useState(false); 
    
    const alertAdditionalDocs = (isLenderRole() || isTPORole()) && !isReadonlyRole() && item.statusName === REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED;

    const openDetailsModal = e => {
      e?.stopPropagation();
      setShowDetailsModal(true);
    };

    return (
      <div className="request-stack">
        <Accordion defaultActiveKey="0">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
            <div style={{ marginTop: '4px' }}>
              <ContextAwareToggle
                eventKey="1"
                callback={() => onExpand?.()}
                {...(!item.isRead ? {
                  collapsedIcon: (
                    <Tooltip title="Expand to view unread activity" enterDelay={300}>
                      <span style={{ display: 'inline-flex' }}>
                        <CaretCircleDownIcon weight="bold" className="accordion-header-icon unread-icon" />
                      </span>
                    </Tooltip>
                  ),
                } : {})}
              />
            </div>
            <div className="request-info">
              <div className="request-prop">
                <div className="request-prop-header">Request Type</div>
                <div className="request-prop-value">{item.requestTypeName}</div>
              </div>
              <div className="request-prop">
                <div className="request-prop-header">Requested By</div>
                <div className="request-prop-value">{item.requestCreatedByName || item.requestCreatedByLdapId}</div>
              </div>
              <div className="request-prop">
                <div className="request-prop-header">Lender</div>
                <div className="request-prop-value">{item.lenderName}</div>
              </div>
              <div className="request-prop">
                <div className="request-prop-header">Date</div>
                <div className="request-prop-value">{moment(item.requestCreatedDate).format("MM/DD/YY")}</div>
              </div>
              
              {(isInternal() || isProjectReadOnlyRole()) ? (                
                <div className="request-prop">
                  <div className="request-prop-header">Assigned To</div>
                  <div className="request-prop-value">
                    {item.assignedToName || item.assignedToLdapId || "N/A"} {(isInternal() && !isClosedStatus(item.statusName) && item.statusName !== REQUEST_STATUSES.UNRESPONSIVE) && (
                      <FaPen 
                        className="feed-edit-icon"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setShowAssignModal(true);
                        }} 
                      />
                    )}
                    {modalInitialized && (
                      <AssignReviewerModal 
                        selectedRequest={item} 
                        show={showAssignModal} 
                        setShow={setShowAssignModal}
                        reviewers={reviewers}
                        onAssignComplete={onRefresh}
                      />
                    )}
                  </div>
                </div>
              ) : (<></>)}
              <div className="request-prop">
                <div className="request-prop-header">Status</div>
                <div className={"request-prop-value " + (alertAdditionalDocs ? "warning" : "")}>
                  {(isInternal() || isProjectReadOnlyRole())
                    // PS/internal: show internalStatus. For Draft, internalStatus='' 
                    // (blank in DB) so fall back to statusName to show 'Draft' label.
                    ? (item.internalStatus || item.statusName || '')
                    // Lender/TPO: show externalStatus. Fall back to statusName in case
                    // externalStatus is blank or the endpoint doesn't return it.
                    : item.internalStatus === REQUEST_STATUSES.ADDITIONAL_INFORMATION_RECEIVED
                      ? "Additional Information Submitted"
                      : (item.externalStatus || item.statusName || '')}
                </div>
              </div>
              <div className="view-details-cell">
                <Button className="btn-submit" onClick={openDetailsModal}>
                  Details
                </Button>
                <RequestDetailsModal
                  show={showDetailsModal}
                  onClose={() => setShowDetailsModal(false)}
                  request={item}
                  projectInfo={projectInfo}
                />
              </div>
            </div>
          </div>
          <Accordion.Collapse eventKey="1">
            <>
              <hr style={{margin: '16px 0', borderColor: '#edebe9'}} />
              <CommunicationFeed requestId={item.requestId} requestStatus={item.statusName} />
              <hr style={{margin: '16px 0', borderColor: '#edebe9'}} />
              <DocumentInfo item={item} />
              <hr style={{margin: '16px 0', borderColor: '#edebe9'}} />
              <ActivityInfo item={item} />
            </>
          </Accordion.Collapse>
        </Accordion>
      </div>
    );
  }, []);

  function DocumentInfo({ item }) { 
    const documents = item.requestFeedDocumentDTOList || [];

    return (
      <div className="request-stack">
        <Accordion defaultActiveKey="0">
          <ContextAwareToggle eventKey="1">
            <p className="request-stack-header accordion-header-title">Documents</p>
          </ContextAwareToggle>
          <Accordion.Collapse eventKey="1">
            <div className="request-stack-list documents-stack-list">
              {documents.map((docItem, i) => (
                <DocumentItem key={i} docItem={docItem} />
              ))}
            </div>
          </Accordion.Collapse>
        </Accordion>
      </div>
    );
  }

  function ActivityInfo ({ item }) {
    const changeLog = item.requestFeedActivityTrackerDTOList || [];
    const shouldFilterExternalUnderReview = !isInternal() && !isProjectReadOnlyRole();

    const toTimestamp = (value) => {
      if (!value) {
        return NaN;
      }

      const parsed = new Date(value).getTime();
      return Number.isNaN(parsed) ? NaN : parsed;
    };

    const firstWithDate = changeLog.find(eventItem => !Number.isNaN(toTimestamp(eventItem?.createdDate)));
    const secondWithDate = changeLog.find(
      eventItem =>
        eventItem !== firstWithDate &&
        !Number.isNaN(toTimestamp(eventItem?.createdDate))
    );

    const hasComparableDates = firstWithDate && secondWithDate;
    const isNewestFirst = hasComparableDates
      ? toTimestamp(firstWithDate.createdDate) > toTimestamp(secondWithDate.createdDate)
      : true;

    const filteredChangeLog = shouldFilterExternalUnderReview
      ? changeLog.filter((eventItem, index) => {
          if (eventItem.externalStatus !== REQUEST_STATUSES.UNDER_REVIEW) {
            return true;
          }

        if (isNewestFirst) {
          if (index === changeLog.length - 1) {
            return true;
          }

          return changeLog[index + 1].externalStatus !== REQUEST_STATUSES.UNDER_REVIEW;
          }

        if (index === 0) {
          return true;
        }

        return changeLog[index - 1].externalStatus !== REQUEST_STATUSES.UNDER_REVIEW;
        })
      : changeLog;

    return (
      <div className="request-stack">
        <Accordion defaultActiveKey="0">
          <ContextAwareToggle eventKey="1">
            <p className="request-stack-header accordion-header-title">Activity</p>
          </ContextAwareToggle>
          <Accordion.Collapse eventKey="1">
            <div className="request-stack-list">
              {filteredChangeLog.map((eventItem, i) => (
                <React.Fragment key={i}>
                  {i > 0 ? <hr style={{margin: '8px 0'}} /> : <></>}
                  <RequestHistoryItem 
                    eventItem={eventItem} 
                    externalStatus={(!isInternal() && !isProjectReadOnlyRole() && eventItem.isEscalated) ? item.externalStatus : undefined}
                  />
                </React.Fragment>
              ))}
            </div>
          </Accordion.Collapse>
        </Accordion>
      </div>
    );
  }

  return (
    <>
    {requests.map((item, i) => {
      // Prepare per-request arrays inside the map
      const changeLog = item.requestFeedActivityTrackerDTOList || [];
      const documents = item.requestFeedDocumentDTOList || [];

      return (      
        <div className={"request-list-item" + (!item.isRead && !expandedRef.current.has(item.requestId) ? " unread" : "")} key={i}>
          <RequestHeaderInfo
            item={item}
            modalInitialized={modalInitialized}
            reviewers={reviewers}
            documents={documents}
            changeLog={changeLog}
            projectRestrictions={projectRestrictions}
            projectInfo={projectInfo}
            onRefresh={onRefresh}
            onExpand={() => handleExpand(item.requestId)}
          />        
        </div>
      );
    })}
  </>
  );
};

export default RequestFeed;